import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useParams, Link } from "react-router-dom";
import { Minus, Plus, ArrowLeft, ArrowRight, Check, Copy, MapPin, Calendar, MessageCircle } from "lucide-react";
import { api, waLink, formatErr } from "@/lib/api";
import { toast } from "sonner";

export default function Booking() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [tours, setTours] = useState([]);
  const [tourId, setTourId] = useState(sp.get("tour") || "");
  const [tour, setTour] = useState(null);
  const [p, setP] = useState({ adults: 2, children: 0, infants: 0, disabled_children: 0 });
  const [form, setForm] = useState({ customer_name: "", phone: "", email: "", preferred_channel: "whatsapp", locality: "", notes: "", accessibility_notes: "", consent_terms: false, consent_privacy: false, consent_marketing: false });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/tours").then(r => setTours(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (tourId) setTour(tours.find(t => t.id === tourId)); }, [tourId, tours]);

  const seats = p.adults + p.children + p.infants + p.disabled_children;
  const total = tour ? (p.adults + p.children) * tour.price_adult : 0;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", { tour_id: tourId, participants: p, ...form, payment_method: "at_venue" });
      setResult(data);
      setStep(4);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  if (result && step === 4) {
    const b = result.booking;
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-cream-50 rounded-[32px] p-8 lg:p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-leaf/30 flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8 text-forest-900" /></div>
          <h1 className="font-serif text-3xl lg:text-4xl text-forest-900">Rezervarea este confirmată!</h1>
          <p className="mt-3 text-muted">Pregătiți-vă pentru o întâlnire pe care o veți povesti mult timp.</p>
          <div className="mt-8 p-6 bg-forest-900 text-cream-50 rounded-2xl text-left">
            <div className="text-xs uppercase tracking-wider text-tucan font-bold">Cod rezervare</div>
            <div className="font-serif text-3xl mt-1 flex items-center gap-2">{b.public_code}
              <button data-testid="copy-code" onClick={() => { navigator.clipboard.writeText(b.public_code); toast.success("Copiat!"); }} className="text-tucan hover:text-white"><Copy className="w-5 h-5" /></button>
            </div>
            <div className="mt-4 text-sm space-y-1">
              <div><strong>Tur:</strong> {b.tour_title}</div>
              <div><strong>Data:</strong> {b.tour_date} · <strong>Ora:</strong> {b.tour_time}</div>
              <div><strong>Participanți:</strong> {b.seats_total} · <strong>Total:</strong> {b.total} RON (plată la locație)</div>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-2 justify-center">
            <a href="https://maps.google.com/?q=47.17939,27.34896" target="_blank" rel="noreferrer" className="border-2 border-forest-900 text-forest-900 rounded-full px-5 py-2 font-semibold flex items-center gap-1"><MapPin className="w-4 h-4" /> Google Maps</a>
            <a href={waLink(`Rezervarea mea la Habitatul Meu: ${b.public_code}`)} target="_blank" rel="noreferrer" className="bg-[#25D366] text-white rounded-full px-5 py-2 font-semibold flex items-center gap-1"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
          </div>
          <p className="mt-6 text-xs text-muted">Recomandare: ajungeți cu 10–15 minute înainte. Verificați regulile de interacțiune pe pagina fiecărui animal.</p>
          <Link to="/" className="mt-8 inline-block text-forest-900 font-semibold underline underline-offset-4">Înapoi la pagina principală</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      <h1 className="font-serif text-3xl lg:text-4xl text-forest-900 tracking-tight">Rezervă un tur</h1>
      <div className="flex gap-2 mt-4 mb-8">
        {[1,2,3].map(n => <div key={n} className={`h-1 flex-1 rounded-full ${step >= n ? "bg-tucan" : "bg-forest-900/10"}`} />)}
      </div>

      {step === 1 && (
        <div className="bg-cream-50 rounded-[24px] p-6 lg:p-8">
          <h2 className="font-serif text-xl text-forest-900 mb-4">1. Alege turul</h2>
          <div className="space-y-2">
            {tours.filter(t => t.seats_available > 0).map(t => (
              <button key={t.id} data-testid={`select-tour-${t.id}`} onClick={() => setTourId(t.id)} className={`w-full text-left p-4 rounded-2xl border-2 transition ${tourId === t.id ? "border-tucan bg-tucan/5" : "border-forest-900/10 bg-white"}`}>
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-serif text-lg text-forest-900">{new Date(t.date).toLocaleDateString('ro-RO', {weekday: 'long', day: 'numeric', month: 'long'})}</div>
                    <div className="text-sm text-muted">{t.start_time} · {t.duration_minutes} min · {t.seats_available} locuri</div>
                  </div>
                  <div className="font-semibold text-forest-900">{t.price_adult} RON</div>
                </div>
              </button>
            ))}
          </div>
          <button data-testid="btn-next-1" disabled={!tourId} onClick={() => setStep(2)} className="mt-6 w-full bg-forest-900 text-cream-50 rounded-full py-3 font-semibold disabled:opacity-40 flex items-center justify-center gap-2">Continuă <ArrowRight className="w-4 h-4" /></button>
        </div>
      )}

      {step === 2 && (
        <div className="bg-cream-50 rounded-[24px] p-6 lg:p-8">
          <h2 className="font-serif text-xl text-forest-900 mb-4">2. Participanți</h2>
          {[
            { key: "adults", label: "Adulți", sub: `${tour?.price_adult || 40} RON` },
            { key: "children", label: "Copii peste 3 ani", sub: `${tour?.price_adult || 40} RON` },
            { key: "infants", label: "Copii 0–3 ani", sub: "Gratuit" },
            { key: "disabled_children", label: "Copii cu dizabilități", sub: "Gratuit" },
          ].map(row => (
            <div key={row.key} className="flex items-center justify-between py-3 border-b border-forest-900/10">
              <div>
                <div className="font-medium text-forest-900">{row.label}</div>
                <div className="text-xs text-muted">{row.sub}</div>
              </div>
              <div className="flex items-center gap-3">
                <button data-testid={`dec-${row.key}`} onClick={() => setP({...p, [row.key]: Math.max(0, p[row.key]-1)})} className="w-9 h-9 rounded-full border border-forest-900/20 flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                <div className="w-6 text-center font-semibold">{p[row.key]}</div>
                <button data-testid={`inc-${row.key}`} onClick={() => setP({...p, [row.key]: p[row.key]+1})} className="w-9 h-9 rounded-full bg-forest-900 text-cream-50 flex items-center justify-center"><Plus className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          <div className="mt-4 flex justify-between font-serif text-xl text-forest-900">
            <span>Total ({seats} pers.)</span><span>{total} RON</span>
          </div>
          <div className="mt-6 flex gap-2">
            <button onClick={() => setStep(1)} className="border-2 border-forest-900 text-forest-900 rounded-full px-5 py-3 font-semibold flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Înapoi</button>
            <button data-testid="btn-next-2" disabled={seats === 0} onClick={() => setStep(3)} className="flex-1 bg-forest-900 text-cream-50 rounded-full py-3 font-semibold disabled:opacity-40 flex items-center justify-center gap-2">Continuă <ArrowRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="bg-cream-50 rounded-[24px] p-6 lg:p-8">
          <h2 className="font-serif text-xl text-forest-900 mb-4">3. Date de contact</h2>
          <div className="space-y-3">
            <input data-testid="input-name" placeholder="Nume și prenume *" value={form.customer_name} onChange={e => setForm({...form, customer_name: e.target.value})} className="w-full rounded-2xl border border-forest-900/20 px-4 py-3 bg-white outline-none focus:border-turaco" />
            <input data-testid="input-phone" placeholder="Telefon *" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full rounded-2xl border border-forest-900/20 px-4 py-3 bg-white outline-none focus:border-turaco" />
            <input data-testid="input-email" placeholder="Email (opțional)" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full rounded-2xl border border-forest-900/20 px-4 py-3 bg-white outline-none focus:border-turaco" />
            <textarea placeholder="Observații (opțional)" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="w-full rounded-2xl border border-forest-900/20 px-4 py-3 bg-white outline-none focus:border-turaco min-h-[80px]" />
            <textarea placeholder="Nevoi de acces sau sprijin (opțional, discret)" value={form.accessibility_notes} onChange={e => setForm({...form, accessibility_notes: e.target.value})} className="w-full rounded-2xl border border-forest-900/20 px-4 py-3 bg-white outline-none focus:border-turaco" />
          </div>
          <div className="mt-4 space-y-2 text-sm">
            <label className="flex items-start gap-2 cursor-pointer"><input data-testid="consent-terms" type="checkbox" checked={form.consent_terms} onChange={e => setForm({...form, consent_terms: e.target.checked})} className="mt-1" /> Sunt de acord cu termenii și politica de anulare *</label>
            <label className="flex items-start gap-2 cursor-pointer"><input data-testid="consent-privacy" type="checkbox" checked={form.consent_privacy} onChange={e => setForm({...form, consent_privacy: e.target.checked})} className="mt-1" /> Sunt de acord cu politica de confidențialitate (GDPR) *</label>
            <label className="flex items-start gap-2 cursor-pointer"><input type="checkbox" checked={form.consent_marketing} onChange={e => setForm({...form, consent_marketing: e.target.checked})} className="mt-1" /> Doresc să primesc noutăți și oferte (opțional)</label>
          </div>
          <div className="mt-6 flex gap-2">
            <button onClick={() => setStep(2)} className="border-2 border-forest-900 text-forest-900 rounded-full px-5 py-3 font-semibold flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Înapoi</button>
            <button data-testid="btn-submit-booking" disabled={submitting || !form.customer_name || !form.phone || !form.consent_terms || !form.consent_privacy} onClick={submit} className="flex-1 bg-tucan text-white rounded-full py-3 font-semibold disabled:opacity-40">
              {submitting ? "Se procesează..." : `Confirmă rezervarea · ${total} RON la locație`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function BookingLookup() {
  const { code } = useParams();
  const [b, setB] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/bookings/${code}`).then(r => setB(r.data)).catch(e => setErr(formatErr(e.response?.data?.detail))); }, [code]);
  if (err) return <div className="max-w-2xl mx-auto px-4 py-16 text-center"><p className="text-red-600">{err}</p></div>;
  if (!b) return <div className="max-w-2xl mx-auto px-4 py-16">Se încarcă...</div>;
  return (
    <div className="max-w-2xl mx-auto px-4 py-16">
      <h1 className="font-serif text-3xl text-forest-900">Rezervarea ta</h1>
      <div className="mt-6 bg-cream-50 rounded-3xl p-8">
        <div className="text-xs uppercase text-turaco font-bold">Cod {b.public_code}</div>
        <div className="font-serif text-2xl text-forest-900 mt-2">{b.tour_title}</div>
        <div className="mt-4 space-y-1 text-forest-900">
          <div><strong>Data:</strong> {b.tour_date} · {b.tour_time}</div>
          <div><strong>Participanți:</strong> {b.seats_total}</div>
          <div><strong>Total:</strong> {b.total} RON</div>
          <div><strong>Status:</strong> {b.status}</div>
        </div>
      </div>
    </div>
  );
}
